// src/supabase.js
// Supabase client — single instance used throughout the entire app

import { createClient } from '@supabase/supabase-js';
import { EMAILS_ENABLED } from './config';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check your .env.local file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// ── Auth helpers ──────────────────────────────────────────────

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  // Try to get the user profile from our users table
  try {
    const { data: profile, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single();
    if (!error && profile) return profile;
  } catch(e) {
    console.warn('DB profile fetch failed, using auth data');
  }
  // Fallback — build profile from auth data
  // Determine role from email for now
  const email = user.email || '';
  let role = 'client';
  if (email === 'brian@bbintegrated.com') role = 'master';
  else if (email.endsWith('@bbintegrated.com')) role = 'sales';
  return {
    id: user.id,
    email: user.email,
    name: user.user_metadata?.name || email.split('@')[0],
    role,
    tier: 'free',
  };
}

// ── User helpers ──────────────────────────────────────────────

export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

// ── Client helpers ────────────────────────────────────────────

export async function getClientsForAdvisor(advisorId) {
  const { data, error } = await supabase
    .from('clients')
    .select(`
      *,
      user:users!clients_user_id_fkey(id, name, email),
      projects(id, name, phase, status, progress, risk, deadline),
      invoices(id, status, amount),
      documents(id, status)
    `)
    .eq('advisor_id', advisorId);
  if (error) throw error;
  return data;
}

export async function getAllClients() {
  const { data, error } = await supabase
    .from('clients')
    .select(`
      *,
      user:users!clients_user_id_fkey(id, name, email),
      advisor:users!clients_advisor_id_fkey(id, name, email),
      projects(id, name, phase, status, progress, risk, deadline),
      invoices(id, status, amount),
      documents(id, status)
    `);
  if (error) throw error;
  return data;
}

export async function getClientForUser(userId) {
  // First try with join
  try {
    const { data, error } = await supabase
      .from('clients')
      .select(`
        *,
        user:users!clients_user_id_fkey(id, name, email),
        advisor:users!clients_advisor_id_fkey(id, name, email)
      `)
      .eq('user_id', userId)
      .single();
    if (!error && data) return data;
  } catch(e) {}
  
  // Fallback — simple lookup without join
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .eq('user_id', userId)
    .single();
  if (error) throw error;
  
  // Fetch user separately
  const { data: userData } = await supabase
    .from('users')
    .select('id, name, email')
    .eq('id', userId)
    .single();
  
  return { ...data, user: userData };
}

export async function updateClient(clientId, updates) {
  const { data, error } = await supabase
    .from('clients')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', clientId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Assessment helpers ────────────────────────────────────────

export async function getAssessment(clientId) {
  const { data, error } = await supabase
    .from('assessments')
    .select('*')
    .eq('client_id', clientId);
  if (error) throw error;
  return data;
}

export async function upsertAssessmentScore(clientId, questionId, bucket, lens, field, score, note) {
  // field is either 'client' or 'advisor'
  const updates = {
    client_id: clientId,
    question_id: questionId,
    bucket,
    lens,
    [`${field}_score`]: score,
    [`${field}_note`]: note,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from('assessments')
    .upsert(updates, { onConflict: 'client_id,question_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Project helpers ───────────────────────────────────────────

export async function getProjects(clientId) {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createProject(clientId, project) {
  const { data, error } = await supabase
    .from('projects')
    .insert({ client_id: clientId, ...project })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateProject(projectId, updates) {
  const { data, error } = await supabase
    .from('projects')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', projectId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Notes helpers ─────────────────────────────────────────────

export async function getNotes(projectId) {
  const { data, error } = await supabase
    .from('notes')
    .select('*, author:users!notes_author_id_fkey(name)')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

export async function addNote(projectId, clientId, authorRole, authorId, text) {
  const { data, error } = await supabase
    .from('notes')
    .insert({ project_id: projectId, client_id: clientId, author_role: authorRole, author_id: authorId, text })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Messages helpers ──────────────────────────────────────────

export async function getMessages(clientId) {
  const { data, error } = await supabase
    .from('messages')
    .select('*, from:users!messages_from_id_fkey(name)')
    .eq('client_id', clientId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

export async function sendMessage(clientId, fromRole, fromId, text) {
  const { data, error } = await supabase
    .from('messages')
    .insert({ client_id: clientId, from_role: fromRole, from_id: fromId, text })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Subscribe to real-time messages
export function subscribeToMessages(clientId, callback) {
  return supabase
    .channel(`messages:${clientId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `client_id=eq.${clientId}`,
    }, payload => callback(payload.new))
    .subscribe();
}

// ── Files helpers ─────────────────────────────────────────────

export async function getFiles(clientId) {
  const { data, error } = await supabase
    .from('files')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addFile(clientId, file) {
  const { data, error } = await supabase
    .from('files')
    .insert({ client_id: clientId, ...file })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateFile(fileId, updates) {
  const { data, error } = await supabase
    .from('files')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', fileId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function uploadFileToStorage(clientId, fileName, fileBlob) {
  const path = `${clientId}/${Date.now()}_${fileName}`;
  const { data, error } = await supabase.storage
    .from('client-files')
    .upload(path, fileBlob);
  if (error) throw error;
  return data.path;
}

// ── Documents helpers ─────────────────────────────────────────

export async function getDocuments(clientId) {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createDocument(clientId, doc) {
  const { data, error } = await supabase
    .from('documents')
    .insert({ client_id: clientId, ...doc })
    .select()
    .single();
  if (error) throw error;
  await addTimelineEntry(clientId, {
    type: 'milestone',
    title: `Document sent for signature: ${data.title}`,
    body: '',
    date: new Date().toISOString().split('T')[0],
  });
  return data;
}

export async function signDocument(documentId, signedBy) {
  const { data, error } = await supabase
    .from('documents')
    .update({
      status: 'signed',
      signed_by: signedBy,
      signed_date: new Date().toISOString().slice(0, 10),
      updated_at: new Date().toISOString(),
    })
    .eq('id', documentId)
    .select()
    .single();
  if (error) throw error;
  await addTimelineEntry(data.client_id, {
    type: 'milestone',
    title: `Document signed: ${data.title}`,
    body: `Signed by ${signedBy}`,
    date: new Date().toISOString().split('T')[0],
  });
  return data;
}

export async function updateDocument(documentId, updates) {
  const { data, error } = await supabase
    .from('documents')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', documentId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteDocument(documentId, storagePath) {
  if (storagePath) {
    await supabase.storage.from('client-files').remove([storagePath]);
  }
  const { error } = await supabase.from('documents').delete().eq('id', documentId);
  if (error) throw error;
}

// ── Invoices helpers ──────────────────────────────────────────

export async function getInvoices(clientId) {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createInvoice(clientId, invoice) {
  const { data, error } = await supabase
    .from('invoices')
    .insert({ client_id: clientId, ...invoice })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function markInvoicePaid(invoiceId) {
  const { data, error } = await supabase
    .from('invoices')
    .update({
      status: 'paid',
      paid_date: new Date().toISOString().slice(0, 10),
      updated_at: new Date().toISOString(),
    })
    .eq('id', invoiceId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function markInvoiceUnpaid(invoiceId) {
  const { data, error } = await supabase
    .from('invoices')
    .update({
      status: 'unpaid',
      paid_date: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', invoiceId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Title / amount / due_date only. status and paid_date are owned by the
// markInvoicePaid / markInvoiceUnpaid helpers — keep those code paths
// separate so a generic edit can't accidentally flip payment state.
export async function updateInvoice(invoiceId, patch) {
  const allowed = {};
  if (patch.title !== undefined) allowed.title = patch.title;
  if (patch.amount !== undefined) allowed.amount = patch.amount;
  if (patch.due_date !== undefined) allowed.due_date = patch.due_date;
  const { data, error } = await supabase
    .from('invoices')
    .update({ ...allowed, updated_at: new Date().toISOString() })
    .eq('id', invoiceId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Hard delete. If a storage_path is provided we best-effort remove the
// attachment first; storage failures are logged but don't block the row
// delete (orphaned blob is recoverable, half-deleted row isn't).
export async function deleteInvoice(invoiceId, storagePath) {
  if (storagePath) {
    try {
      const { error: storageErr } = await supabase.storage.from('client-files').remove([storagePath]);
      if (storageErr) console.warn('deleteInvoice: storage remove failed:', storageErr.message || storageErr);
    } catch (e) {
      console.warn('deleteInvoice: storage remove threw:', e);
    }
  }
  const { error } = await supabase.from('invoices').delete().eq('id', invoiceId);
  if (error) throw error;
  return true;
}

// ── Resources helpers ─────────────────────────────────────────

export async function getResources(clientId) {
  const { data, error } = await supabase
    .from('resources')
    .select('*')
    .or(`scope.eq.global,client_id.eq.${clientId}`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addResource(resource) {
  const { data, error } = await supabase
    .from('resources')
    .insert(resource)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Sessions helpers ──────────────────────────────────────────

export async function getSessions(clientId) {
  const { data, error } = await supabase
    .from('sessions')
    .select('*, items:session_items(*)')
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

export async function createSession(session) {
  const { data, error } = await supabase
    .from('sessions')
    .insert(session)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function addSessionItems(items) {
  const { data, error } = await supabase
    .from('session_items')
    .insert(items)
    .select();
  if (error) throw error;
  return data;
}

// ── Checklist helpers ─────────────────────────────────────────

export async function getChecklist(clientId) {
  const { data, error } = await supabase
    .from('checklist_items')
    .select('*')
    .eq('client_id', clientId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data;
}

export async function addChecklistItem(clientId, text, sortOrder) {
  const { data, error } = await supabase
    .from('checklist_items')
    .insert({ client_id: clientId, text, custom: true, sort_order: sortOrder })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function toggleChecklistItem(itemId, done) {
  const { data, error } = await supabase
    .from('checklist_items')
    .update({ done, updated_at: new Date().toISOString() })
    .eq('id', itemId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function seedChecklist(clientId) {
  const template = [
    { text: 'Create your portal account',                          auto: true,  done: true,  sort_order: 1 },
    { text: 'Review and sign engagement agreement',                auto: false, done: false, sort_order: 2 },
    { text: 'Complete the BB Integrated Business Assessment',      auto: false, done: false, sort_order: 3 },
    { text: 'Schedule your first advisor session',                 auto: false, done: false, sort_order: 4 },
    { text: 'Review your gap analysis results',                    auto: false, done: false, sort_order: 5 },
    { text: 'Approve your initial project list',                   auto: false, done: false, sort_order: 6 },
  ];
  const { data, error } = await supabase
    .from('checklist_items')
    .insert(template.map(t => ({ client_id: clientId, ...t })))
    .select();
  if (error) throw error;
  return data;
}

// ── Timeline helpers ──────────────────────────────────────────

export async function getTimeline(clientId) {
  const { data, error } = await supabase
    .from('timeline')
    .select('*')
    .eq('client_id', clientId)
    .order('date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function addTimelineEntry(clientId, entry) {
  const { data, error } = await supabase
    .from('timeline')
    .insert({ client_id: clientId, ...entry })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Quick answers helpers ─────────────────────────────────────

export async function saveQuickAnswers(clientId, categoryId, answers) {
  const rows = Object.entries(answers).map(([questionId, answer]) => ({
    client_id: clientId,
    category_id: categoryId,
    question_id: questionId,
    answer: Array.isArray(answer) ? answer.join(', ') : answer,
  }));
  const { data, error } = await supabase
    .from('quick_answers')
    .upsert(rows, { onConflict: 'client_id,category_id,question_id' })
    .select();
  if (error) throw error;
  return data;
}

// ── Usage tracking helpers ────────────────────────────────────

export async function getUsageRecord(userId) {
  const month = new Date().toISOString().slice(0, 7);
  const { data, error } = await supabase
    .from('usage_tracking')
    .select('*')
    .eq('user_id', userId)
    .eq('month', month)
    .single();
  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = not found
  return data;
}

export async function incrementUsage(userId, feature) {
  const month = new Date().toISOString().slice(0, 7);
  // Upsert — create record if doesn't exist, increment if it does
  const { data: existing } = await supabase
    .from('usage_tracking')
    .select('*')
    .eq('user_id', userId)
    .eq('month', month)
    .single();

  if (existing) {
    const { data, error } = await supabase
      .from('usage_tracking')
      .update({
        [feature]: (existing[feature] || 0) + 1,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('usage_tracking')
      .insert({
        user_id: userId,
        month,
        [feature]: 1,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  }
}

export async function requestUpgrade(userId) {
  const { data, error } = await supabase
    .from('advisors')
    .update({
      upgrade_requested: true,
      upgrade_requested_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Delete helpers ────────────────────────────────────────────

export async function deleteFile(fileId, storagePath) {
  if (storagePath) {
    await supabase.storage.from('client-files').remove([storagePath]);
  }
  const { error } = await supabase.from('files').delete().eq('id', fileId);
  if (error) throw error;
}

export async function deleteResource(resourceId) {
  const { error } = await supabase.from('resources').delete().eq('id', resourceId);
  if (error) throw error;
}

export async function deleteProject(projectId) {
  const { error } = await supabase.from('projects').delete().eq('id', projectId);
  if (error) throw error;
}

export async function deleteNote(noteId) {
  const { error } = await supabase.from('notes').delete().eq('id', noteId);
  if (error) throw error;
}

export async function updateNote(noteId, text) {
  const { error } = await supabase.from('notes').update({ text }).eq('id', noteId);
  if (error) throw error;
}

// ── Thoughts / braindump helpers ──────────────────────────────

export async function getThoughts(clientId) {
  const { data, error } = await supabase
    .from('checklist_items')
    .select('*')
    .eq('client_id', clientId)
    .eq('auto', false)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function addThought(clientId, title, text, authorRole) {
  const { data, error } = await supabase
    .from('checklist_items')
    .insert({
      client_id: clientId,
      text: JSON.stringify({ title, text, authorRole }),
      auto: false,
      custom: true,
      done: false,
      sort_order: Math.floor(Date.now() / 1000),
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteThought(thoughtId) {
  const { error } = await supabase.from('checklist_items').delete().eq('id', thoughtId);
  if (error) throw error;
}

export async function markThoughtDone(thoughtId, done) {
  const { data, error } = await supabase
    .from('checklist_items')
    .update({ done, updated_at: new Date().toISOString() })
    .eq('id', thoughtId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Persist the JSON payload (title/text/authorRole/submitted) for an existing thought.
// Used by the inline "Edit" button on a ThoughtCard.
export async function updateThoughtText(thoughtId, payload) {
  const { data, error } = await supabase
    .from('checklist_items')
    .update({ text: JSON.stringify(payload), updated_at: new Date().toISOString() })
    .eq('id', thoughtId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Archive / unarchive set the two new columns (archived, archived_at). Archived
// rows stay in the DB until the 30-day purge runs.
export async function archiveThought(thoughtId) {
  const { data, error } = await supabase
    .from('checklist_items')
    .update({ archived: true, archived_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('id', thoughtId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function unarchiveThought(thoughtId) {
  const { data, error } = await supabase
    .from('checklist_items')
    .update({ archived: false, archived_at: null, updated_at: new Date().toISOString() })
    .eq('id', thoughtId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Delete archived thoughts whose archived_at is older than 30 days.
// Called at thought-load time — no cron job needed.
export async function purgeExpiredArchivedThoughts(clientId) {
  const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const { error } = await supabase
    .from('checklist_items')
    .delete()
    .eq('client_id', clientId)
    .eq('archived', true)
    .lt('archived_at', cutoff);
  if (error) throw error;
}

// Shared helper: creates a project row + matching timeline milestone entry.
// Single source of truth for "create a project from a note / suggestion / gap" —
// replaces three near-identical inline copies that previously existed.
export async function createProjectFromThought(clientId, name, description, extras = {}) {
  const proj = await createProject(clientId, {
    name,
    description: description || '',
    phase: extras.phase || 'Clarity',
    status: extras.status || 'active',
    progress: extras.progress ?? 0,
    risk: extras.risk || 'medium',
    deadline: extras.deadline || 'TBD',
  });
  const d = new Date();
  const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  await addTimelineEntry(clientId, {
    type: 'milestone',
    title: `Project created: ${proj.name}`,
    body: (description || '').slice(0, 200),
    date: dateStr,
  });
  return proj;
}

// ── Project checklist helpers ─────────────────────────────────

export async function getProjectChecklist(projectId) {
  const { data, error } = await supabase
    .from('project_checklist_items')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function addProjectChecklistItem(projectId, clientId, text, ownerRole, priority='medium', dueDate=null, parentId=null, ownerId=null) {
  const { data, error } = await supabase
    .from('project_checklist_items')
    .insert({ project_id: projectId, client_id: clientId, text, owner_role: ownerRole, done: false, priority, due_date: dueDate, parent_id: parentId, owner_id: ownerId })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateProjectChecklistItem(itemId, updates) {
  const { data, error } = await supabase
    .from('project_checklist_items')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', itemId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function toggleProjectChecklistItem(itemId, done) {
  const { data, error } = await supabase
    .from('project_checklist_items')
    .update({ done, updated_at: new Date().toISOString() })
    .eq('id', itemId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteProjectChecklistItem(itemId) {
  const { error } = await supabase.from('project_checklist_items').delete().eq('id', itemId);
  if (error) throw error;
}

// ── Client logo helpers ───────────────────────────────────────

export async function uploadClientLogo(clientId, file) {
  const ext = file.name.split('.').pop();
  const path = `logos/${clientId}/logo.${ext}`;
  // Remove existing logo first
  await supabase.storage.from('client-files').remove([path]);
  const { data, error } = await supabase.storage.from('client-files').upload(path, file, { upsert: true });
  if (error) throw error;
  // Get public URL
  const { data: urlData } = supabase.storage.from('client-files').getPublicUrl(path);
  // Save to client record
  await updateClient(clientId, { logo_url: urlData.publicUrl });
  return urlData.publicUrl;
}

export async function deleteClientLogo(clientId) {
  await supabase.storage.from('client-files').remove([`logos/${clientId}/logo.png`, `logos/${clientId}/logo.jpg`, `logos/${clientId}/logo.jpeg`, `logos/${clientId}/logo.webp`]);
  await updateClient(clientId, { logo_url: null });
}

// ── Assessment reopen ─────────────────────────────────────────

export async function reopenAdvisorReview(clientId) {
  const { data, error } = await supabase
    .from('clients')
    .update({ advisor_review_complete: false, updated_at: new Date().toISOString() })
    .eq('id', clientId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Client invite / PIN system ────────────────────────────────

export async function createClientWithPin(advisorId, { firstName, lastName, company, email, pin, selectedCategories, hiddenTabs }) {
  // Generate unique invite token
  const inviteToken = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);

  // Create client record without a user_id yet (they'll claim it on first login)
  const { data, error } = await supabase
    .from('clients')
    .insert({
      advisor_id: advisorId,
      company: company || `${firstName} ${lastName}`,
      phase: 'Clarity',
      phase_num: 1,
      selected_categories: selectedCategories || [],
      invite_token: inviteToken,
      invite_pin: pin,
      invite_claimed: false,
      temp_name: `${firstName} ${lastName}`,
      temp_email: email || null,
      hidden_tabs: hiddenTabs || [],
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getClientByInviteToken(token) {
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .eq('invite_token', token)
    .single();
  if (error) return null;
  return data;
}

export async function claimClientInvite(inviteToken, pin, { email, password, firstName, lastName }) {
  if (!EMAILS_ENABLED) console.warn('[email-audit] supabase.auth.signUp called from claimClientInvite while EMAILS_ENABLED=false. Confirm "Confirm signup" template is disabled in Supabase. See EMAIL_AUDIT.md.');
  // Phase 1: create the auth user. If they already exist (e.g., a prior
  // claim that was later reset — user_id was nulled but auth.users still
  // holds the row), signUp errors with "already registered" — swallow
  // that and let the signIn step below recover the session. Mirrors the
  // team-member claim flow.
  const { error: signUpError } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name: `${firstName} ${lastName}` } }
  });
  if (signUpError && !/already|registered|exists/i.test(signUpError.message || '')) {
    throw signUpError;
  }

  // Phase 2: explicit signInWithPassword. Two purposes:
  //   (a) Recovers from signUp errors when the user already exists
  //       (post-reset re-claim with same email).
  //   (b) Settles the session before the edge-function call below, which
  //       authenticates the caller from its JWT — signUp's in-memory session
  //       can lose to the next request's anon JWT; signInWithPassword's full
  //       handshake fixes that.
  const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (signInError) {
    throw new Error('Sign-in failed. If you already claimed this portal before, use the same password as before. ' + (signInError.message || ''));
  }
  if (!signInData.session) {
    throw new Error('Sign-in returned no active session. Confirm Supabase Auth → Confirm email is DISABLED.');
  }

  if (!signInData.user?.id) throw new Error('Failed to obtain user account id');

  // Phase 3: authorization is server-owned. The claim-invite edge function
  // verifies the pin, derives the role from the matched invite table, and
  // performs both writes (the public.users grant, then the invite bind) with
  // the service_role key. The browser no longer writes public.users or
  // clients, and never supplies a role — a `role` key at any depth in the
  // body is rejected server-side.
  let invokeResult;
  try {
    invokeResult = await supabase.functions.invoke('claim-invite', {
      body: { inviteToken, pin, firstName, lastName }
    });
  } catch (e) {
    throw new Error('claim-invite invoke threw: ' + (e?.message || e));
  }

  const { data, error } = invokeResult;
  if (error) {
    // On a non-2xx the SDK leaves `data` null and error.message is only the
    // generic "Edge Function returned a non-2xx status code". The structured
    // { error, code } body is on error.context, which may be absent, already
    // consumed, or unparseable — so recovery is best-effort.
    let body = null;
    try {
      body = await error.context.json();
    } catch {
      body = null;
    }
    const messageForCode = {
      invalid_pin: 'Incorrect PIN. Please try again.',
      invite_not_found: 'Invalid invite link. Please contact your advisor.',
      invite_bound_to_other_user: 'This invite has already been claimed by another account.',
      role_conflict: 'This account already has a different role. Contact your advisor.',
      role_conflict_master: 'This account already has a different role. Contact your advisor.',
      bind_conflict: 'Could not complete your invitation. Please try again or contact your advisor.',
      unauthorized: 'Your session expired. Please try again.',
    }[body?.code];
    if (messageForCode) throw new Error(messageForCode);
    if (body?.error) throw new Error(body.error);
    throw new Error(error.message);
  }

  if (!data || data.status !== 'ok') {
    throw new Error('Claim did not complete as expected.');
  }

  return signInData;
}

// Supersedes the inline 11-table cascade in App.js handleDeleteClient.
// Fixes two bugs in the old path: (1) the client's auth.users row was left
// orphaned, and (2) team_member_profiles for the client's team members
// were never cleaned up (leaving their auth logins active too).
// Edge-function failures are non-fatal — same trade-off as deleteAdvisor:
// an orphaned auth.users row is recoverable; a half-deleted client whose
// old login still works is worse.
export async function deleteClientCascade(clientId) {
  // 1. Look up the client's linked auth user BEFORE deleting the row.
  const { data: client, error: clientLookupErr } = await supabase
    .from('clients')
    .select('user_id')
    .eq('id', clientId)
    .maybeSingle();
  if (clientLookupErr) throw clientLookupErr;
  const clientUserId = client?.user_id || null;

  // 2. Collect team-member auth user_ids for this client BEFORE cascade.
  const { data: teamMembers, error: tmLookupErr } = await supabase
    .from('team_member_profiles')
    .select('user_id')
    .eq('client_id', clientId);
  if (tmLookupErr) throw tmLookupErr;
  const teamMemberUserIds = (teamMembers || [])
    .map(tm => tm?.user_id)
    .filter(Boolean);

  // 3. Run the original 10-table dependent cascade, in the same order
  //    as the old inline version (clients itself is deleted last in step 5).
  const cascadeTables = [
    'project_checklist_items',
    'notes',
    'timeline',
    'invoices',
    'resources',
    'messages',
    'sessions',
    'assessments',
    'checklist_items',
    'projects',
  ];
  for (const table of cascadeTables) {
    const { error } = await supabase.from(table).delete().eq('client_id', clientId);
    if (error) throw error;
  }

  // 4. Delete team_member_profiles for this client. team_member_access
  //    rows clear automatically via ON DELETE CASCADE on team_member_id.
  const { error: tmDelErr } = await supabase
    .from('team_member_profiles')
    .delete()
    .eq('client_id', clientId);
  if (tmDelErr) throw tmDelErr;

  // 5. Delete the clients row itself.
  const { error: clientDelErr } = await supabase
    .from('clients')
    .delete()
    .eq('id', clientId);
  if (clientDelErr) throw clientDelErr;

  // 6. Tear down auth.users for the client + any claimed team members.
  //    Same invocation + non-fatal logging pattern as deleteAdvisor.
  const authUserIds = [clientUserId, ...teamMemberUserIds].filter(Boolean);
  for (const userId of authUserIds) {
    try {
      const { data, error } = await supabase.functions.invoke('delete-auth-user', {
        body: { userId },
      });
      if (error) {
        console.error('delete-auth-user edge function failed:', error.message || error);
      } else if (data && data.error) {
        console.error('delete-auth-user returned error:', data.error);
      }
    } catch (e) {
      console.error('delete-auth-user invoke threw:', e);
    }
  }
}

// ── Advisor invite / PIN system ───────────────────────────────

export async function createAdvisorWithPin(masterId, { firstName, lastName, email, pin }) {
  // Generate unique invite token (16 chars from two Math.random base-36 slices)
  const inviteToken = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);

  const { data, error } = await supabase
    .from('advisors')
    .insert({
      invite_token: inviteToken,
      invite_pin: pin,
      invite_claimed: false,
      temp_name: `${firstName} ${lastName}`,
      temp_email: email,
      created_by: masterId,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getAdvisorByInviteToken(token) {
  const { data, error } = await supabase
    .from('advisors')
    .select('*')
    .eq('invite_token', token)
    .single();
  if (error) return null;
  return data;
}

export async function claimAdvisorInvite(inviteToken, pin, { email, password, firstName, lastName }) {
  if (!EMAILS_ENABLED) console.warn('[email-audit] supabase.auth.signUp called from claimAdvisorInvite while EMAILS_ENABLED=false. Confirm "Confirm signup" template is disabled in Supabase. See EMAIL_AUDIT.md.');
  // Phase 1: create the auth user. If they already exist (e.g., a prior
  // claim that was later reset — user_id was nulled but auth.users still
  // holds the row), signUp errors with "already registered" — swallow
  // that and let the signIn step below recover the session. Mirrors the
  // team-member claim flow.
  const { error: signUpError } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name: `${firstName} ${lastName}` } }
  });
  if (signUpError && !/already|registered|exists/i.test(signUpError.message || '')) {
    throw signUpError;
  }

  // Phase 2: explicit signInWithPassword. Two purposes:
  //   (a) Recovers from signUp errors when the user already exists
  //       (post-reset re-claim with same email).
  //   (b) Settles the session before the edge-function call below, which
  //       authenticates the caller from its JWT — signUp's in-memory session
  //       can lose to the next request's anon JWT; signInWithPassword's full
  //       handshake fixes that.
  const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (signInError) {
    throw new Error('Sign-in failed. If you already claimed this advisor account before, use the same password as before. ' + (signInError.message || ''));
  }
  if (!signInData.session) {
    throw new Error('Sign-in returned no active session. Confirm Supabase Auth → Confirm email is DISABLED.');
  }

  if (!signInData.user?.id) throw new Error('Failed to obtain user account id');

  // Phase 3: authorization is server-owned. The claim-invite edge function
  // verifies the pin, derives the role from the matched invite table, and
  // performs both writes (the public.users grant, then the invite bind) with
  // the service_role key. The browser no longer writes public.users or
  // advisors, and never supplies a role — a `role` key at any depth in the
  // body is rejected server-side.
  let invokeResult;
  try {
    invokeResult = await supabase.functions.invoke('claim-invite', {
      body: { inviteToken, pin, firstName, lastName }
    });
  } catch (e) {
    throw new Error('claim-invite invoke threw: ' + (e?.message || e));
  }

  const { data, error } = invokeResult;
  if (error) {
    // On a non-2xx the SDK leaves `data` null and error.message is only the
    // generic "Edge Function returned a non-2xx status code". The structured
    // { error, code } body is on error.context, which may be absent, already
    // consumed, or unparseable — so recovery is best-effort.
    let body = null;
    try {
      body = await error.context.json();
    } catch {
      body = null;
    }
    const messageForCode = {
      invalid_pin: 'Incorrect PIN. Please try again.',
      invite_not_found: 'Invalid invite link. Please contact your master administrator.',
      invite_bound_to_other_user: 'This invite has already been claimed by another account.',
      role_conflict: 'This account already has a different role. Contact your master administrator.',
      role_conflict_master: 'This account already has a different role. Contact your master administrator.',
      bind_conflict: 'Could not complete your invitation. Please try again or contact your master administrator.',
      unauthorized: 'Your session expired. Please try again.',
    }[body?.code];
    if (messageForCode) throw new Error(messageForCode);
    if (body?.error) throw new Error(body.error);
    throw new Error(error.message);
  }

  if (!data || data.status !== 'ok') {
    throw new Error('Claim did not complete as expected.');
  }

  return signInData;
}

export async function getAllAdvisors() {
  const { data, error } = await supabase
    .from('advisors')
    .select(`
      *,
      user:users!advisors_user_id_fkey(id, name, email)
    `)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

// Deletes an advisor row AND (when present) the linked auth.users row.
//
// The auth.users delete goes through the `delete-auth-user` edge function,
// which holds the service_role key server-side and verifies the caller
// has role='master'. See supabase/functions/delete-auth-user/index.ts.
//
// Deployment of the edge function (run from project root):
//   supabase init                                    # one-time, if not done
//   supabase login
//   supabase link --project-ref <YOUR_PROJECT_REF>
//   supabase functions deploy delete-auth-user
// SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY are auto-injected by the
// Supabase platform — no extra `supabase secrets set` needed.
export async function deleteAdvisor(advisorId) {
  // 1. Look up the linked auth user (if any) BEFORE deleting the row.
  const { data: advisor, error: lookupErr } = await supabase
    .from('advisors')
    .select('user_id')
    .eq('id', advisorId)
    .maybeSingle();
  if (lookupErr) throw lookupErr;
  const userId = advisor?.user_id || null;

  // 2. Delete the advisors row. If this fails we abort — auth user stays.
  const { error: delErr } = await supabase.from('advisors').delete().eq('id', advisorId);
  if (delErr) throw delErr;

  // 3. If there was a linked auth user, ask the edge function to remove it.
  //    Failures here are logged but do NOT roll back the advisors-table
  //    delete — leaving an orphaned auth.users row is recoverable; leaving
  //    a half-deleted advisor with a still-active login is worse.
  if (userId) {
    try {
      const { data, error } = await supabase.functions.invoke('delete-auth-user', {
        body: { userId },
      });
      if (error) {
        console.error('delete-auth-user edge function failed:', error.message || error);
      } else if (data && data.error) {
        console.error('delete-auth-user returned error:', data.error);
      }
    } catch (e) {
      console.error('delete-auth-user invoke threw:', e);
    }
  }
}

// Admin-initiated password reset for a client. Sets a new password on the
// target auth.users row via the `admin-set-password` edge function, which
// holds the service_role key server-side and verifies the caller is either
// a master OR the advisor who owns the target client. See
// supabase/functions/admin-set-password/index.ts for full pattern + the
// deployment command.
//
// Throw-on-error semantics: any SDK-level failure (network, non-2xx) OR
// function-level { error } body throws — the UI surface needs to know
// when a reset actually failed. On success returns the function's
// response body, e.g. { ok: true, userId }.
export async function adminSetClientPassword(userId, newPassword) {
  let invokeResult;
  try {
    invokeResult = await supabase.functions.invoke('admin-set-password', {
      body: { userId, password: newPassword },
    });
  } catch (e) {
    throw new Error('admin-set-password invoke threw: ' + (e?.message || e));
  }
  const { data, error } = invokeResult;
  if (error) {
    // SDK-level failure (network error, non-2xx HTTP, function not found).
    throw new Error('admin-set-password edge function failed: ' + (error.message || error));
  }
  if (data && data.error) {
    // Function returned a non-2xx with a JSON body containing { error }.
    throw new Error(data.error);
  }
  if (!data || data.ok !== true) {
    throw new Error('admin-set-password returned an unexpected response');
  }
  return data;
}

// Generic-named wrapper for the same admin-set-password edge function, used
// by the cross-tier admin directory where the target may be any user type
// (client, advisor, team member). Behavior and error semantics are identical
// to adminSetClientPassword — kept as a separate export so the call site
// reads correctly for non-client targets.
export async function adminSetUserPassword(userId, newPassword) {
  let invokeResult;
  try {
    invokeResult = await supabase.functions.invoke('admin-set-password', {
      body: { userId, password: newPassword },
    });
  } catch (e) {
    throw new Error('admin-set-password invoke threw: ' + (e?.message || e));
  }
  const { data, error } = invokeResult;
  if (error) {
    throw new Error('admin-set-password edge function failed: ' + (error.message || error));
  }
  if (data && data.error) {
    throw new Error(data.error);
  }
  if (!data || data.ok !== true) {
    throw new Error('admin-set-password returned an unexpected response');
  }
  return data;
}

// Wrapper for the admin-set-email edge function (deployed in Stage 3b).
// Updates auth.users.email and the appropriate app-table mirror in one
// server-side hop. Same error-handling pattern as the password wrappers.
export async function adminSetUserEmail(userId, email) {
  let invokeResult;
  try {
    invokeResult = await supabase.functions.invoke('admin-set-email', {
      body: { userId, email },
    });
  } catch (e) {
    throw new Error('admin-set-email invoke threw: ' + (e?.message || e));
  }
  const { data, error } = invokeResult;
  if (error) {
    throw new Error('admin-set-email edge function failed: ' + (error.message || error));
  }
  if (data && data.error) {
    throw new Error(data.error);
  }
  if (!data || data.ok !== true) {
    throw new Error('admin-set-email returned an unexpected response');
  }
  return data;
}

// Pre-claim email update for users who have NOT yet claimed an auth account.
// Routing:
//   client     → clients.temp_email          by clients.id
//   advisor    → advisors.temp_email         by advisors.id
//   teamMember → team_member_profiles.email  by team_member_profiles.id
//                (team members share one column for both phases; see
//                src/lib/userEmail.js for the rationale.)
// For CLAIMED users, do NOT call this — use adminSetUserEmail() instead so
// auth.users and the app-table mirror update atomically server-side.
export async function updatePreClaimEmail(type, rowId, email) {
  const TABLES = { client: 'clients', advisor: 'advisors', teamMember: 'team_member_profiles' };
  const table = TABLES[type];
  if (!table) throw new Error('updatePreClaimEmail: unknown type ' + type);
  const column = type === 'teamMember' ? 'email' : 'temp_email';
  const payload = { [column]: email || null, updated_at: new Date().toISOString() };
  const { error } = await supabase.from(table).update(payload).eq('id', rowId);
  if (error) throw error;
  return true;
}

// ── Team Member invite / access system ────────────────────────
// Phase 3: page-level + per-item access.
//   - team_member_profiles.page_access (text[]) — which sections are toggled on
//   - team_member_access rows — per-item grants for project/file/doc/resource/invoice
// The FK on team_member_access.team_member_id references
// team_member_profiles.id ON DELETE CASCADE (Phase 3 migration), so access
// can be written at invite time and profile deletion auto-cleans access rows.
// Assessment is binary (page_access only); no team_member_access rows for it.

const TEAM_PAGE_TYPES = ['projects', 'files', 'docs', 'resources', 'billing'];

// Page key ↔ resource_type mapping. The team_member_access.resource_type CHECK
// uses singular names; page_access uses plural page keys.
const RESOURCE_TYPE_BY_PAGE = {
  projects: 'project',
  files: 'file',
  docs: 'doc',
  resources: 'resource',
  billing: 'invoice',
  // assessment intentionally excluded — no picker, no access rows.
};
const PAGE_KEY_BY_RESOURCE_TYPE = Object.fromEntries(
  Object.entries(RESOURCE_TYPE_BY_PAGE).map(([k, v]) => [v, k])
);
const PER_ITEM_PAGE_KEYS = Object.keys(RESOURCE_TYPE_BY_PAGE);

function emptyItemAccess() {
  return PER_ITEM_PAGE_KEYS.reduce((m, k) => { m[k] = []; return m; }, {});
}

// Master-tier directory query: all team-member profiles across all clients,
// joined to their client for context (company name + advisor ownership).
// RLS is expected to permit master role to read all rows.
export async function getAllTeamMembers() {
  const { data, error } = await supabase
    .from('team_member_profiles')
    .select('*, client:clients(id, company, advisor_id)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getTeamMembersForClient(clientId) {
  const { data: profiles, error } = await supabase
    .from('team_member_profiles')
    .select('*')
    .eq('client_id', clientId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  const list = profiles || [];

  // Collect item-level grants for these profiles in one query.
  const accessByProfile = {};
  if (list.length > 0) {
    const ids = list.map(p => p.id);
    const { data: accessRows, error: aErr } = await supabase
      .from('team_member_access')
      .select('team_member_id, resource_type, resource_id')
      .in('team_member_id', ids);
    if (aErr) throw aErr;
    (accessRows || []).forEach(r => {
      const pageKey = PAGE_KEY_BY_RESOURCE_TYPE[r.resource_type];
      if (!pageKey) return;
      if (!accessByProfile[r.team_member_id]) accessByProfile[r.team_member_id] = emptyItemAccess();
      accessByProfile[r.team_member_id][pageKey].push(r.resource_id);
    });
  }

  // Per-client item counts so row pills can render "X of Y" and the form
  // can disable toggles for empty resource types.
  const counts = await getClientItemCounts(clientId);

  return list.map(p => ({
    ...p,
    itemAccess: accessByProfile[p.id] || emptyItemAccess(),
    clientItemCounts: counts,
  }));
}

// Advisor-tier directory query: all team-member profiles across every client
// owned by this advisor, with the parent client's company name attached as
// `clientCompany` for breadcrumb display in the admin directory.
export async function getTeamMembersForAdvisor(advisorId) {
  const { data: ownClients, error: clientsErr } = await supabase
    .from('clients')
    .select('id, company')
    .eq('advisor_id', advisorId);
  if (clientsErr) throw clientsErr;
  if (!ownClients || ownClients.length === 0) return [];

  const perClient = await Promise.all(
    ownClients.map(c => getTeamMembersForClient(c.id))
  );

  const out = [];
  ownClients.forEach((c, i) => {
    (perClient[i] || []).forEach(tm => {
      out.push({ ...tm, client_id: c.id, clientCompany: c.company || null });
    });
  });
  return out;
}

// Counts of pickable items per page type for a client. Resources include
// globals + client-scoped (mirrors getResources). Assessment returns 1/0.
export async function getClientItemCounts(clientId) {
  const [proj, fil, doc, res, inv, ass] = await Promise.all([
    supabase.from('projects').select('*', { count: 'exact', head: true }).eq('client_id', clientId),
    supabase.from('files').select('*', { count: 'exact', head: true }).eq('client_id', clientId),
    supabase.from('documents').select('*', { count: 'exact', head: true }).eq('client_id', clientId),
    supabase.from('resources').select('*', { count: 'exact', head: true }).or(`scope.eq.global,client_id.eq.${clientId}`),
    supabase.from('invoices').select('*', { count: 'exact', head: true }).eq('client_id', clientId),
    supabase.from('assessments').select('*', { count: 'exact', head: true }).eq('client_id', clientId),
  ]);
  return {
    projects: proj.count || 0,
    files: fil.count || 0,
    docs: doc.count || 0,
    resources: res.count || 0,
    billing: inv.count || 0,
    assessment: (ass.count || 0) > 0 ? 1 : 0,
  };
}

// Lists of pickable items per page type, shaped for the Select Access modal.
// Each item: {id, name, subtitle}. Skips assessment (no picker).
export async function getClientResourcesForPicker(clientId) {
  const [projects, files, docs, resources, invoices] = await Promise.all([
    supabase.from('projects').select('id, name, status, phase').eq('client_id', clientId).order('created_at', { ascending: false }),
    supabase.from('files').select('id, name, category, size_label').eq('client_id', clientId).order('created_at', { ascending: false }),
    supabase.from('documents').select('id, title, status').eq('client_id', clientId).order('created_at', { ascending: false }),
    supabase.from('resources').select('id, title, type, scope').or(`scope.eq.global,client_id.eq.${clientId}`).order('created_at', { ascending: false }),
    supabase.from('invoices').select('id, title, amount, status, due_date').eq('client_id', clientId).order('created_at', { ascending: false }),
  ]);
  return {
    projects: (projects.data || []).map(p => ({
      id: p.id,
      name: p.name || 'Untitled project',
      subtitle: [p.phase, p.status].filter(Boolean).join(' · '),
    })),
    files: (files.data || []).map(f => ({
      id: f.id,
      name: f.name || 'Untitled file',
      subtitle: [f.category, f.size_label].filter(Boolean).join(' · '),
    })),
    docs: (docs.data || []).map(d => ({
      id: d.id,
      name: d.title || 'Untitled document',
      subtitle: d.status || '',
    })),
    resources: (resources.data || []).map(r => ({
      id: r.id,
      name: r.title || 'Untitled resource',
      subtitle: [r.type, r.scope === 'global' ? 'global' : null].filter(Boolean).join(' · '),
    })),
    billing: (invoices.data || []).map(i => ({
      id: i.id,
      name: i.title || 'Untitled invoice',
      subtitle: [i.amount ? `$${i.amount}` : null, i.status].filter(Boolean).join(' · '),
    })),
  };
}

export async function createTeamMember(clientId, invitedBy, { name, email, jobTitle, pin, pageAccess, inviteMessageTemplate, itemAccess, invitedByName }) {
  const inviteToken = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2));
  const validAccess = (pageAccess || []).filter(t => TEAM_PAGE_TYPES.includes(t));
  const { data, error } = await supabase
    .from('team_member_profiles')
    .insert({
      client_id: clientId,
      name,
      email,
      job_title: jobTitle || null,
      invite_token: inviteToken,
      invite_pin: pin,
      invite_claimed: false,
      invite_message_template: inviteMessageTemplate || null,
      invited_by: invitedBy || null,
      invited_by_name: invitedByName || null,
      page_access: validAccess,
    })
    .select()
    .single();
  if (error) throw error;
  if (itemAccess) {
    await syncTeamMemberAccess(data.id, itemAccess, invitedBy || null);
  }
  return data;
}

export async function updateTeamMember(profileId, { name, jobTitle, pageAccess, inviteMessageTemplate, itemAccess, grantedBy }) {
  const updates = { updated_at: new Date().toISOString() };
  if (name !== undefined) updates.name = name;
  if (jobTitle !== undefined) updates.job_title = jobTitle || null;
  if (inviteMessageTemplate !== undefined) updates.invite_message_template = inviteMessageTemplate;
  if (pageAccess !== undefined) {
    updates.page_access = (pageAccess || []).filter(t => TEAM_PAGE_TYPES.includes(t));
  }
  const { data, error } = await supabase
    .from('team_member_profiles')
    .update(updates)
    .eq('id', profileId)
    .select()
    .single();
  if (error) throw error;
  if (itemAccess !== undefined) {
    await syncTeamMemberAccess(profileId, itemAccess, grantedBy || null);
  }
  return data;
}

// Bulk-replaces team_member_access rows for one profile, across all per-item
// page types. Strategy: wipe all per-item rows for this profile, then bulk-
// insert from the passed itemAccess map. Assessment is ignored.
export async function syncTeamMemberAccess(profileId, itemAccess, grantedBy) {
  const resourceTypes = PER_ITEM_PAGE_KEYS.map(k => RESOURCE_TYPE_BY_PAGE[k]);
  await supabase
    .from('team_member_access')
    .delete()
    .eq('team_member_id', profileId)
    .in('resource_type', resourceTypes);
  const rows = [];
  PER_ITEM_PAGE_KEYS.forEach(pageKey => {
    const resType = RESOURCE_TYPE_BY_PAGE[pageKey];
    const ids = (itemAccess && itemAccess[pageKey]) || [];
    ids.forEach(resourceId => {
      rows.push({
        team_member_id: profileId,
        resource_type: resType,
        resource_id: resourceId,
        granted_by: grantedBy || null,
      });
    });
  });
  if (rows.length > 0) {
    const { error } = await supabase.from('team_member_access').insert(rows);
    if (error) throw error;
  }
}

// Full team-member teardown: profile row + linked auth.users row.
// team_member_access clears automatically via ON DELETE CASCADE on
// team_member_id (Phase 3 migration). Edge-function failure is
// non-fatal — same trade-off as deleteAdvisor / deleteClientCascade:
// an orphaned auth.users row is recoverable; a half-deleted profile
// whose old login still works is worse.
export async function deleteTeamMember(profileId) {
  // 1. Look up the linked auth user BEFORE deleting the profile row.
  const { data: profile, error: lookupErr } = await supabase
    .from('team_member_profiles')
    .select('user_id')
    .eq('id', profileId)
    .maybeSingle();
  if (lookupErr) throw lookupErr;
  const userId = profile?.user_id || null;

  // 2. Delete the profile (cascades to team_member_access).
  const { error } = await supabase
    .from('team_member_profiles')
    .delete()
    .eq('id', profileId);
  if (error) throw error;

  // 3. Tear down auth.users for claimed members. Non-fatal logging
  //    pattern matches deleteAdvisor / deleteClientCascade.
  if (userId) {
    try {
      const { data, error } = await supabase.functions.invoke('delete-auth-user', {
        body: { userId },
      });
      if (error) {
        console.error('delete-auth-user edge function failed:', error.message || error);
      } else if (data && data.error) {
        console.error('delete-auth-user returned error:', data.error);
      }
    } catch (e) {
      console.error('delete-auth-user invoke threw:', e);
    }
  }
  return true;
}

// ── Team Member claim flow (Phase 4) ──────────────────────────
// Anonymous PIN entry with DB-backed lockout, then signUp + profile link,
// then a one-time welcome page. RLS policies set in the Phase 4 migration:
//   - anon SELECT on unclaimed team_member_profiles by invite_token
//   - anon UPDATE (column-level: claim_attempts, claim_locked_at only)
//   - authenticated UPDATE to self-claim (sets user_id + invite_claimed)
//   - authenticated UPDATE for welcome_seen_at + future self-edits
//   - authenticated SELECT on self profile, linked client, own access rows

const PIN_MAX_ATTEMPTS = 5;

// Anon-callable. Returns null if no unclaimed invite matches the token
// (already-claimed invites are hidden by RLS).
export async function getTeamMemberProfileByInviteToken(token) {
  const { data, error } = await supabase
    .from('team_member_profiles')
    .select('*')
    .eq('invite_token', token)
    .maybeSingle();
  if (error) {
    console.warn('[team-claim] profile lookup failed:', error.message);
    return null;
  }
  return data;
}

// Verifies PIN against the profile. On mismatch, increments claim_attempts
// (and sets claim_locked_at when attempts reach PIN_MAX_ATTEMPTS). Anon-
// callable via the column-level UPDATE grant on those two columns.
// Returns one of:
//   { ok: true,  profile }
//   { ok: false, locked: true,  profile }
//   { ok: false, locked: false, profile, attemptsRemaining }
export async function verifyTeamMemberPin(profile, pin) {
  if (profile.claim_locked_at) {
    return { ok: false, locked: true, profile };
  }
  if (profile.invite_pin === pin) {
    return { ok: true, profile };
  }
  const newAttempts = (profile.claim_attempts || 0) + 1;
  const updates = { claim_attempts: newAttempts };
  if (newAttempts >= PIN_MAX_ATTEMPTS) {
    updates.claim_locked_at = new Date().toISOString();
  }
  const { data, error } = await supabase
    .from('team_member_profiles')
    .update(updates)
    .eq('id', profile.id)
    .select()
    .single();
  if (error) {
    console.warn('[team-claim] attempt increment failed:', error.message);
    return {
      ok: false,
      locked: false,
      profile,
      attemptsRemaining: Math.max(0, PIN_MAX_ATTEMPTS - newAttempts),
    };
  }
  return {
    ok: false,
    locked: !!data.claim_locked_at,
    profile: data,
    attemptsRemaining: Math.max(0, PIN_MAX_ATTEMPTS - newAttempts),
  };
}

// Claim: signs up (or recovers an existing auth user via signIn), then
// hands off to the SECURITY DEFINER RPC for atomic PIN verify +
// public.users upsert + team_member_profiles link.
//
// Why not the simpler signUp + direct UPDATE flow?
//   - supabase-js has a documented JWT propagation race after signUp:
//     the first PostgREST request can ship with the anon JWT instead
//     of the freshly-issued one, silently failing under RLS. The
//     explicit signInWithPassword below settles the session
//     deterministically before any subsequent PostgREST call.
//   - The RPC also closes a PIN-bypass security gap that an inline
//     UPDATE under RLS can't enforce (RLS WITH CHECK can verify
//     user_id = auth.uid() but can't check that the caller supplied
//     the correct PIN). Defense in depth.
export async function claimTeamMemberInvite(profile, { email, password, firstName, lastName, pin }) {
  if (!EMAILS_ENABLED) {
    console.warn('[email-audit] supabase.auth.signUp called from claimTeamMemberInvite while EMAILS_ENABLED=false. Confirm "Confirm signup" template is disabled in Supabase. See EMAIL_AUDIT.md.');
  }
  const fullName = `${firstName} ${lastName}`.trim();

  // Phase 1: create the auth user. If they already exist (e.g., a prior
  // orphaned attempt), signUp errors with "already registered" — swallow
  // that and let the signIn step below recover the session.
  const { error: signUpError } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name: fullName, role: 'team_member' } },
  });
  if (signUpError && !/already|registered|exists/i.test(signUpError.message || '')) {
    throw signUpError;
  }

  // Phase 2: explicit signInWithPassword. Two purposes:
  //   (a) Recovers from signUp errors when the user already exists (orphan retry).
  //   (b) Fixes the documented JWT propagation race: signUp returns a session in
  //       memory, but the very next supabase-js request can still go out with the
  //       anon JWT. signInWithPassword performs a full auth handshake whose
  //       response settles the session before the RPC call below.
  const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (signInError) {
    throw new Error('Sign-in failed. If you already started registering with this invite, use the same password as before. ' + (signInError.message || ''));
  }
  if (!signInData.session) {
    throw new Error('Sign-in returned no active session. Confirm Supabase Auth → Confirm email is DISABLED.');
  }

  // Phase 3: atomic claim via SECURITY DEFINER RPC. Verifies the PIN
  // server-side, upserts public.users, and links team_member_profiles
  // in one transaction — bypassing the RLS dance that failed previously.
  const { data, error } = await supabase.rpc('claim_team_member_invite_rpc', {
    p_profile_id: profile.id,
    p_pin: pin,
    p_name: fullName,
  });
  if (error) throw error;
  if (!data) throw new Error('Claim RPC returned no profile id.');

  return signInData;
}

// Welcome page payload: the authenticated team member's own profile,
// the linked client (for company name + logo), and item-access counts
// derived from team_member_access rows. We deliberately avoid joining
// projects/files/etc — that would depend on Phase 5 read policies on
// those tables.
export async function getMyTeamMemberContext() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // 1. Own profile + linked client (RLS: team_member_profiles_self_read,
  //    clients_team_member_read).
  const { data: profile, error: pErr } = await supabase
    .from('team_member_profiles')
    .select('*, client:clients(id, company, logo_url)')
    .eq('user_id', user.id)
    .maybeSingle();
  if (pErr) {
    console.warn('[team-welcome] profile fetch failed:', pErr.message);
    return null;
  }
  if (!profile) return null;

  // 2. Own per-item access rows (RLS: team_member_access_self_read).
  const { data: accessRows } = await supabase
    .from('team_member_access')
    .select('resource_type')
    .eq('team_member_id', profile.id);
  const itemCounts = {};
  (accessRows || []).forEach(r => {
    itemCounts[r.resource_type] = (itemCounts[r.resource_type] || 0) + 1;
  });

  return { profile, client: profile.client || null, itemCounts };
}

export async function markTeamMemberWelcomeSeen(profileId) {
  const { error } = await supabase
    .from('team_member_profiles')
    .update({ welcome_seen_at: new Date().toISOString() })
    .eq('id', profileId);
  if (error) throw error;
}

export async function sendPasswordResetEmail(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
}

export async function getClientCheckslistProgress(clientId) {
  // Get all projects for client and their checklist completion
  const { data: projects } = await supabase.from('projects').select('id').eq('client_id', clientId);
  if (!projects?.length) return {};
  
  const progress = {};
  for (const proj of projects) {
    const { data: items } = await supabase.from('project_checklist_items').select('id,done,parent_id').eq('project_id', proj.id);
    const topLevel = (items||[]).filter(i=>!i.parent_id);
    const done = topLevel.filter(i=>i.done).length;
    progress[proj.id] = topLevel.length > 0 ? Math.round((done/topLevel.length)*100) : 0;
  }
  return progress;
}

// ── Recent activity per client ────────────────────────────────

export async function getRecentActivityForClients(clientIds) {
  if(!clientIds||clientIds.length===0) return {};
  // Get the most recent timeline entry per client from the last 7 days
  const since = new Date(Date.now() - 7*24*60*60*1000).toISOString();
  const { data } = await supabase
    .from('timeline')
    .select('client_id, created_at, title')
    .in('client_id', clientIds)
    .gte('created_at', since)
    .order('created_at', { ascending: false });
  
  // Group by client_id, keep most recent
  const result = {};
  for(const row of (data||[])){
    if(!result[row.client_id]){
      result[row.client_id] = { count: 0, latest: row.created_at, title: row.title };
    }
    result[row.client_id].count++;
  }
  return result;
}

// ── Per-project card data (projects list page) ────────────────
// Activity source = max(project_checklist_items.updated_at, notes.created_at) per project.
// timeline is intentionally excluded — it's client-scoped (no project_id column),
// so it can't cleanly attribute activity to a single project.
export async function getProjectCardData(clientId) {
  const { data: projects } = await supabase
    .from('projects')
    .select('id')
    .eq('client_id', clientId);
  if (!projects?.length) return {};
  const projectIds = projects.map(p => p.id);

  const [itemsRes, notesRes] = await Promise.all([
    supabase
      .from('project_checklist_items')
      .select('project_id, text, done, due_date, updated_at, parent_id')
      .in('project_id', projectIds)
      .is('parent_id', null),
    supabase
      .from('notes')
      .select('project_id, created_at, author:users!notes_author_id_fkey(name)')
      .in('project_id', projectIds)
      .order('created_at', { ascending: false }),
  ]);

  const itemsByProject = {};
  for (const item of (itemsRes.data || [])) {
    (itemsByProject[item.project_id] = itemsByProject[item.project_id] || []).push(item);
  }
  const latestNoteByProject = {};
  for (const note of (notesRes.data || [])) {
    if (!latestNoteByProject[note.project_id]) {
      latestNoteByProject[note.project_id] = note;
    }
  }

  const result = {};
  for (const pid of projectIds) {
    const items = itemsByProject[pid] || [];
    const topTotal = items.length;
    const topDone = items.filter(i => i.done).length;

    const open = items.filter(i => !i.done);
    open.sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return a.due_date.localeCompare(b.due_date);
    });
    const nextItem = open[0] || null;

    let latestItemAt = null;
    for (const item of items) {
      if (item.updated_at && (!latestItemAt || item.updated_at > latestItemAt)) {
        latestItemAt = item.updated_at;
      }
    }
    const latestNote = latestNoteByProject[pid] || null;
    const latestNoteAt = latestNote?.created_at || null;

    let lastActivityAt = null;
    let lastActivitySource = null;
    let lastActivityAuthorName = null;
    if (latestItemAt && (!latestNoteAt || latestItemAt >= latestNoteAt)) {
      lastActivityAt = latestItemAt;
      lastActivitySource = 'checklist';
    } else if (latestNoteAt) {
      lastActivityAt = latestNoteAt;
      lastActivitySource = 'note';
      lastActivityAuthorName = latestNote?.author?.name || null;
    }

    result[pid] = { topTotal, topDone, nextItem, lastActivityAt, lastActivitySource, lastActivityAuthorName };
  }

  return result;
}

// ── Advisor Knowledge Base helpers ────────────────────────────

/**
 * Get all knowledge base entries accessible to a given advisor.
 * Returns universal entries (advisor_id = NULL) plus the advisor's own entries.
 * Master users see everything.
 */
export async function getKnowledgeBase(advisorId, isMaster = false) {
  let query = supabase
    .from('advisor_knowledge')
    .select('*')
    .order('category', { ascending: true })
    .order('title', { ascending: true });
  
  if (!isMaster) {
    // Sales advisors see universal + their own
    query = query.or(`advisor_id.is.null,advisor_id.eq.${advisorId}`);
  }
  // Master sees all rows (RLS handles this; no filter needed)
  
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Create a new knowledge base entry.
 * Pass advisorId for advisor-specific entries, or null for universal.
 */
export async function createKnowledgeEntry({ category, title, content, tags = [], advisorId = null }) {
  const { data, error } = await supabase
    .from('advisor_knowledge')
    .insert({
      advisor_id: advisorId,
      category,
      title,
      content,
      tags,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Update an existing knowledge base entry.
 */
export async function updateKnowledgeEntry(entryId, updates) {
  const { data, error } = await supabase
    .from('advisor_knowledge')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', entryId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Delete a knowledge base entry.
 */
export async function deleteKnowledgeEntry(entryId) {
  const { error } = await supabase
    .from('advisor_knowledge')
    .delete()
    .eq('id', entryId);
  if (error) throw error;
}

/**
 * Get a filtered subset of the knowledge base for AI prompt context.
 * Returns entries based on the categories requested and the scope (advisor or client).
 *
 * For advisor-mode AI features: pulls everything available to the advisor.
 * For client-mode AI features (future Phase 2): we'll filter out voice_guidelines,
 *   coaching_method, faith_in_practice, and all advisor-specific entries.
 */
export async function getKnowledgeForPrompt(advisorId, { mode = 'advisor', categories = null } = {}) {
  let query = supabase
    .from('advisor_knowledge')
    .select('category, title, content, tags');
  
  if (mode === 'client') {
    // Client mode: only universal entries, exclude sensitive categories
    const excludedCategories = ['voice_guidelines', 'coaching_method', 'faith_in_practice'];
    query = query
      .is('advisor_id', null)
      .not('category', 'in', `(${excludedCategories.map(c => `"${c}"`).join(',')})`);
  } else {
    // Advisor mode: universal + their own entries
    query = query.or(`advisor_id.is.null,advisor_id.eq.${advisorId}`);
  }
  
  if (categories && categories.length > 0) {
    query = query.in('category', categories);
  }
  
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Get all distinct categories currently in use in the knowledge base.
 * Useful for the Admin UI's category dropdown.
 */
export async function getKnowledgeCategories() {
  const { data, error } = await supabase
    .from('advisor_knowledge')
    .select('category');
  if (error) throw error;
  // Distinct list
  const unique = [...new Set((data || []).map(r => r.category))];
  return unique.sort();
}
